import { useState } from 'react';
import { Alert, Button, Card, Modal, ModalDialog, Stack, Typography } from '@mui/joy';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../lib/api';
import { usePermission } from '../../hooks/usePermission';
import type { Task } from '../../types/tasks';

export function TaskManagementActions({ task, disabled, onFinished }: {
  task: Task; disabled?: boolean; onFinished: () => void;
}) {
  const { can } = usePermission();
  const client = useQueryClient();
  const [action, setAction] = useState<'archive' | 'restore' | 'delete' | null>(null);
  const mutation = useMutation({
    mutationFn: async (operation: 'archive' | 'restore' | 'delete') => {
      if (operation === 'delete') await apiClient.delete(`/api/tasks/${task.id}/`);
      else await apiClient.patch(`/api/tasks/${task.id}/`, { is_archived: operation === 'archive' });
    },
    onSuccess: () => {
      setAction(null);
      onFinished();
      client.removeQueries({ queryKey: ['task', task.id], exact: true });
      for (const key of ['tasks', 'jobTasks', 'opportunityTasks', 'dispatch', 'dispatch-backlog', 'dispatch-details', 'inbox', 'unified-inbox', 'production']) {
        client.invalidateQueries({ queryKey: [key] });
      }
    },
  });
  const choose = (operation: 'archive' | 'restore' | 'delete') => { mutation.reset(); setAction(operation); };
  if (!can('tasks', 'edit') && !can('tasks', 'delete')) return null;
  const title = action === 'delete' ? 'Delete Task' : action === 'restore' ? 'Restore Task' : 'Archive Task';
  const error = mutation.error as { responseData?: { is_archived?: string[]; detail?: string }; message?: string } | null;
  return <>
    <Card variant="outlined" sx={{ mt: 3 }}>
      <Typography level="title-md">Manage Task</Typography>
      <Typography level="body-sm">{task.is_archived ? 'This task is archived. Restore it to return it to active task lists and the calendar when scheduled.' : 'Archive keeps the task and its history without marking it complete. Delete permanently removes it.'}</Typography>
      <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap', mt: 1 }}>
        {can('tasks', 'edit') && <Button variant="outlined" color="neutral" disabled={disabled} onClick={() => choose(task.is_archived ? 'restore' : 'archive')}>{task.is_archived ? 'Restore Task' : 'Archive Task'}</Button>}
        {can('tasks', 'delete') && <Button variant="outlined" color="danger" disabled={disabled} onClick={() => choose('delete')}>Delete Task</Button>}
      </Stack>
    </Card>
    <Modal open={!!action} onClose={() => !mutation.isPending && setAction(null)}>
      <ModalDialog role="alertdialog" aria-labelledby="task-management-title" sx={{ width: 460, maxWidth: 'calc(100vw - 32px)' }}>
        <Typography id="task-management-title" level="h4">{title}?</Typography>
        <Typography level="title-sm" sx={{ overflowWrap: 'anywhere' }}>{task.name}</Typography>
        <Typography level="body-sm">{action === 'delete'
          ? 'This permanently deletes the task, its comments, attachments and time entries. Linked jobs, opportunities and contacts are kept. This cannot be undone.'
          : action === 'restore' ? 'The task will return with its existing status, due date and history.'
          : 'Remove this task from active lists and the calendar. Its status and history will be preserved. You can restore it from Tasks → Task Visibility → Archived Tasks.'}</Typography>
        {action === 'delete' && <Typography level="body-sm">Any generated recurring tasks belonging to this task will also be deleted.</Typography>}
        {mutation.isError && <Alert color="danger">{error?.responseData?.is_archived?.[0] || error?.responseData?.detail || error?.message || 'The task could not be updated. Please retry.'}</Alert>}
        <Stack direction="row" sx={{ gap: 1, justifyContent: 'flex-end' }}>
          <Button variant="plain" color="neutral" disabled={mutation.isPending} onClick={() => setAction(null)}>Cancel</Button>
          <Button color={action === 'delete' ? 'danger' : 'primary'} loading={mutation.isPending} onClick={() => action && mutation.mutate(action)}>{title}</Button>
        </Stack>
      </ModalDialog>
    </Modal>
  </>;
}
